<template>
  <MoleculesTitleCard heading="カテゴリ別ニュース">
    <div class="flex flex-wrap">
      <div
        v-for="item in items"
        :key="item.heading"
        class="w-full not-last:border-b md:not-last:border-b-0 md:w-1/2 md:odd:border-r md:not-last-two:border-b border-gray-300 p-4"
      >
        <!-- NOTE: external: falseにするとスムーススクロールが効いてしまいページ遷移したかがわかりずらくなってしまうため、trueにしておく -->
        <NuxtLink
          class="flex mb-5"
          :to="{
            path: '/public/article/news',
            query: item.query,
          }"
          :external="true"
        >
          <span
            class="block rounded-full border-[#fc3f63] border-4 w-5 h-5 shrink-0 mr-2"
          />
          <span class="font-bold underline">{{ item.heading }}</span>
        </NuxtLink>

        <div v-for="(article, i) in item.articles" :key="article.permalink!">
          <NuxtLink
            v-if="i === 0"
            class="flex items-start hover:underline hover:opacity-60 duration-300 mb-5"
            :to="createArticleLink(article.permalink!)"
          >
            <div class="shrink-0 w-1/4 rounded-lg mr-4 overflow-hidden">
              <img
                v-if="article.image_file"
                :src="
                  createArticleImageUrl({
                    path: article.image_file,
                    w: 150,
                    h: 150,
                  })
                "
              />
              <AtomsPlaceholderImage v-else class="w-full" />
            </div>
            <p class="font-bold">
              {{ article.title }}
              <span
                v-if="isPrArticle(article)"
                class="border border-current p-0.5 ml-1 text-xs"
                >PR</span
              >
            </p>
          </NuxtLink>

          <NuxtLink
            v-else
            class="hover:underline hover:opacity-60 duration-300 mb-1 flex"
            :to="createArticleLink(article.permalink!)"
          >
            <span class="block shrink-0 mr-3">
              <TriangleIcon
                :size="10"
                class="text-brown-400 rotate-90 block relative top-[5px]"
              />
            </span>
            <span class="text-ellipsis overflow-hidden text-nowrap">
              <span
                v-if="isPrArticle(article)"
                class="border border-current p-0.5 text-xs mr-1"
                >PR</span
              >
              <span>{{ article.title }}</span></span
            >
          </NuxtLink>
        </div>
      </div>
    </div>
  </MoleculesTitleCard>
</template>

<script setup lang="ts">
import type { LocationQueryRaw } from '#vue-router';
import type { GetArticlesQueryArticleExtended } from '@shared/types/kiwiUserServer';
import TriangleIcon from 'vue-material-design-icons/Triangle.vue';
import { useImageUrl } from '@shared/composables/useImageUrl';

const { createArticleImageUrl } = useImageUrl();
const { getNewsCategoryIdByName, isPrArticle } = await useCategories();

const { data: articlesData } = await useFetch('/api/kiwi_user/articles', {
  query: {
    folder_id: 100,
    limit: 5,
    order_by: 'public_ts',
    direction: 'desc',
  },
});

const { data: rankArticlesData } = await useFetch('/api/kiwi_user/articles', {
  query: {
    folder_id: 100,
    limit: 5,
    order_by: 'page_view',
    direction: 'desc',
  },
});

const { data: recipeArticlesData } = await useFetch('/api/kiwi_user/articles', {
  query: {
    folder_id: 100,
    limit: 5,
    order_by: 'public_ts',
    direction: 'desc',
    category: getNewsCategoryIdByName('食・レシピ'),
  },
});

const { data: exerciseArticlesData } = await useFetch(
  '/api/kiwi_user/articles',
  {
    query: {
      folder_id: 100,
      limit: 5,
      order_by: 'public_ts',
      direction: 'desc',
      category: getNewsCategoryIdByName('エクササイズ'),
    },
  },
);

const { data: dietArticlesData } = await useFetch('/api/kiwi_user/articles', {
  query: {
    folder_id: 100,
    limit: 5,
    order_by: 'public_ts',
    direction: 'desc',
    category: getNewsCategoryIdByName('ダイエット'),
  },
});

const { data: beautyArticlesData } = await useFetch('/api/kiwi_user/articles', {
  query: {
    folder_id: 100,
    limit: 5,
    order_by: 'public_ts',
    direction: 'desc',
    category: getNewsCategoryIdByName('ビューティー'),
  },
});

const { data: seminarArticlesData } = await useFetch(
  '/api/kiwi_user/articles',
  {
    query: {
      folder_id: 100,
      limit: 5,
      order_by: 'public_ts',
      direction: 'desc',
      category: getNewsCategoryIdByName('セミナー・講座'),
    },
  },
);

const { data: menopauseArticlesData } = await useFetch(
  '/api/kiwi_user/articles',
  {
    query: {
      folder_id: 100,
      limit: 5,
      order_by: 'public_ts',
      direction: 'desc',
      category: getNewsCategoryIdByName('更年期対策'),
    },
  },
);

const items = computed<
  {
    heading: string;
    query?: LocationQueryRaw;
    articles: GetArticlesQueryArticleExtended[];
  }[]
>(() => {
  return [
    {
      heading: '新着ニュース',
      articles: articlesData.value?.articles ?? [],
    },
    {
      heading: '週間ランキング',
      query: {
        order: 'rank',
      },
      articles: rankArticlesData.value?.articles ?? [],
    },
    {
      heading: '食・レシピ',
      query: {
        ct: getNewsCategoryIdByName('食・レシピ'),
      },
      articles: recipeArticlesData.value?.articles ?? [],
    },
    {
      heading: 'エクササイズ',
      query: {
        ct: getNewsCategoryIdByName('エクササイズ'),
      },
      articles: exerciseArticlesData.value?.articles ?? [],
    },
    {
      heading: 'ダイエット',
      query: {
        ct: getNewsCategoryIdByName('ダイエット'),
      },
      articles: dietArticlesData.value?.articles ?? [],
    },
    {
      heading: 'ビューティー',
      query: {
        ct: getNewsCategoryIdByName('ビューティー'),
      },
      articles: beautyArticlesData.value?.articles ?? [],
    },
    {
      heading: 'セミナー・講座',
      query: {
        ct: getNewsCategoryIdByName('セミナー・講座'),
      },
      articles: seminarArticlesData.value?.articles ?? [],
    },
    {
      heading: '更年期対策',
      query: {
        ct: getNewsCategoryIdByName('更年期対策'),
      },
      articles: menopauseArticlesData.value?.articles ?? [],
    },
  ];
});
</script>
